(() => {
  if (customElements.get('fox-collage-mobile')) return;

  class FoxCollageMobile extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.banners-collage');
      if (!this.track) return;
      this.slides = this.dataset.blockOrder.split(',').map(id =>
        Array.from(this.track.querySelectorAll('.image-banner')).find(slide => slide.id === `image-banner-block-${id}`)
      ).filter(Boolean);
      if (!this.slides.length) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.mobileQuery = matchMedia('(max-width: 1024px)');
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.designMode = Boolean(window.Shopify?.designMode);
      this.index = 0;
      this.userPaused = false;
      this.allowMotion = false;
      this.visible = false;
      this.interacting = false;
      this.dots = Array.from(this.querySelectorAll('[data-slide-index]'));
      this.controls = this.querySelector('.fox-collage-mobile__controls');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.toggle = this.querySelector('[data-toggle-autoplay]');
      this.status = this.querySelector('[data-slide-status]');
      this.originalAttributes = this.slides.map(slide => ({
        inert: slide.inert, hidden: slide.getAttribute('aria-hidden')
      }));

      // Keep the theme's desktop slider intact, suspending its timers on mobile.
      // Its resize code and original DOM continue to work when returning to desktop.
      this.desktopSliders = Array.from(this.track.querySelectorAll('big-slider')).map(slider => {
        const original = { slider, videoAutoplay: slider.video_autoplay, methods: {} };
        for (const name of ['startAutoplay', 'sliderToLeft', 'sliderToRight']) {
          if (typeof slider[name] !== 'function') continue;
          const method = slider[name];
          original.methods[name] = method;
          slider[name] = (...args) => {
            if (!this.isMobile()) return method.apply(slider, args);
          };
        }
        return original;
      });
      // These listeners prevent the original slider from also handling mobile swipes.
      this.track.querySelectorAll('.big-slider__items').forEach(items => {
        for (const event of ['touchstart', 'touchend', 'mousedown', 'click']) {
          items.addEventListener(event, e => { if (this.isMobile()) e.stopPropagation(); }, { signal });
        }
      });

      this.mobileQuery.addEventListener('change', () => this.updateMode(), { signal });
      this.motionQuery.addEventListener('change', () => {
        this.allowMotion = false;
        this.updateControls();
        this.scheduleAutoplay();
      }, { signal });
      this.previous?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      this.next?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
      this.dots.forEach(dot => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.slideIndex)), { signal }));
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.isPaused();
        this.allowMotion = !this.userPaused;
        if (!this.userPaused && this.dataset.loop !== 'true' && this.index === this.slides.length - 1) {
          this.goTo(0, { manual: false });
        }
        this.updateControls();
        this.scheduleAutoplay();
      }, { signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
      this.track.addEventListener('scrollend', () => this.finishScroll(), { signal });
      this.track.addEventListener('keydown', event => {
        if (!this.isMobile() || event.target !== this.track) return;
        const indices = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in indices)) return;
        event.preventDefault();
        this.goTo(indices[event.key]);
      }, { signal });
      this.track.addEventListener('pointerdown', event => {
        if (!this.isMobile()) return;
        this.interacting = true;
        this.pointerStart = event.clientX;
        this.stopAutoplay();
      }, { signal, passive: true });
      for (const event of ['pointerup', 'pointercancel']) {
        window.addEventListener(event, e => {
          if (!this.interacting) return;
          this.interacting = false;
          if (Math.abs(e.clientX - this.pointerStart) > 10) {
            this.announceSlide = true;
            this.pauseAfterInteraction();
          }
          this.scheduleAutoplay();
        }, { signal, passive: true });
      }
      this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
      this.addEventListener('focusout', () => setTimeout(() => this.scheduleAutoplay(), 0), { signal });
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), { signal });
      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        const index = this.slides.findIndex(slide => slide.id === `image-banner-block-${blockId}`);
        if (index >= 0 && this.isMobile()) this.goTo(index, { behavior: 'instant' });
      }, { signal });

      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.scheduleAutoplay();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.isMobile()) return;
        const width = this.track.clientWidth;
        if (width !== this.trackWidth) {
          this.trackWidth = width;
          this.scrollToIndex('instant');
        }
        this.updateHeight();
      });
      this.resizeObserver.observe(this.track);
      this.slides.forEach(slide => this.resizeObserver.observe(slide));
      this.updateMode();
    }

    isMobile() {
      return this.dataset.enabled === 'true' && this.mobileQuery.matches;
    }

    isPaused() {
      return this.userPaused || (this.motionQuery.matches && !this.allowMotion) || this.designMode;
    }

    updateMode() {
      const active = this.isMobile();
      this.toggleAttribute('data-mobile-active', active);
      if (active) {
        this.setAttribute('role', 'region');
        this.setAttribute('aria-roledescription', 'carousel');
        this.track.setAttribute('tabindex', '0');
        this.track.setAttribute('aria-label', 'Bannere; foloseste sagetile pentru navigare');
        this.desktopSliders.forEach(({ slider }) => {
          clearTimeout(slider.slider);
          slider.video_autoplay = false;
        });
        this.controls.hidden = this.slides.length < 2 || !this.controls.querySelector('button');
        this.updateSlides();
        this.scrollToIndex('instant');
      } else {
        this.stopAutoplay();
        clearTimeout(this.scrollTimer);
        this.track.style.removeProperty('height');
        this.track.removeAttribute('tabindex');
        this.track.removeAttribute('aria-label');
        this.removeAttribute('role');
        this.removeAttribute('aria-roledescription');
        this.controls.hidden = true;
        this.slides.forEach((slide, index) => {
          slide.inert = this.originalAttributes[index].inert;
          const hidden = this.originalAttributes[index].hidden;
          hidden === null ? slide.removeAttribute('aria-hidden') : slide.setAttribute('aria-hidden', hidden);
        });
        this.desktopSliders.forEach(({ slider, videoAutoplay }) => {
          slider.video_autoplay = videoAutoplay;
          if (slider.slider_time) slider.startAutoplay();
        });
      }
      this.scheduleAutoplay();
    }

    slidePosition(index) {
      const left = this.slides[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      return Math.max(0, Math.min(left, this.track.scrollWidth - this.track.clientWidth));
    }

    scrollToIndex(behavior) {
      this.track.scrollTo({ left: this.slidePosition(this.index), behavior });
    }

    goTo(index, { manual = true, behavior } = {}) {
      if (!this.isMobile()) return;
      if (this.dataset.loop === 'true') index = (index + this.slides.length) % this.slides.length;
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.announceSlide = manual;
      this.stopAutoplay();
      if (manual) this.pauseAfterInteraction();
      this.updateSlides();
      this.scrollToIndex(behavior || (this.motionQuery.matches ? 'instant' : 'smooth'));
      // Also restart after selecting the current slide, which produces no scroll event.
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finishScroll(), 150);
    }

    onScroll() {
      if (!this.isMobile()) return;
      this.stopAutoplay();
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finishScroll(), 120);
    }

    finishScroll() {
      if (!this.isMobile() || this.interacting) return;
      clearTimeout(this.scrollTimer);
      this.index = this.slides.reduce((nearest, slide, index) =>
        Math.abs(this.slidePosition(index) - this.track.scrollLeft) < Math.abs(this.slidePosition(nearest) - this.track.scrollLeft) ? index : nearest, 0);
      this.updateSlides();
      this.scheduleAutoplay();
    }

    pauseAfterInteraction() {
      if (this.dataset.pauseInteraction === 'true') this.userPaused = true;
      this.updateControls();
    }

    updateSlides() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video-js, video-youtube-js').forEach(video => video.dispatchEvent(new CustomEvent('stopVideo')));
      });
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      const loop = this.dataset.loop === 'true';
      if (this.previous) this.previous.disabled = !loop && this.index === 0;
      if (this.next) this.next.disabled = !loop && this.index === this.slides.length - 1;
      // Autoplay stays quiet for screen readers; user navigation announces the slide.
      if (this.status) this.status.textContent = this.announceSlide || this.designMode ? `Banner ${this.index + 1} din ${this.slides.length}` : '';
      this.updateControls();
      this.updateHeight();
    }

    updateHeight() {
      const height = Math.ceil(this.slides[this.index].getBoundingClientRect().height);
      if (height > 0 && Math.abs(this.track.getBoundingClientRect().height - height) > 1) this.track.style.height = `${height}px`;
    }

    updateControls() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(this.isPaused()));
      this.toggle.setAttribute('aria-label', this.isPaused() ? 'Porneste derularea automata' : 'Opreste derularea automata');
      this.toggle.disabled = this.designMode;
    }

    stopAutoplay() {
      clearTimeout(this.autoplayTimer);
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      const focusPauses = this.contains(document.activeElement) && document.activeElement !== this.toggle;
      if (!this.controller || !this.isMobile() || this.slides.length < 2 || this.dataset.autoplay !== 'true' ||
          this.isPaused() || !this.visible || document.hidden || this.interacting || focusPauses) return;
      if (this.dataset.loop !== 'true' && this.index === this.slides.length - 1) {
        this.userPaused = true;
        this.updateControls();
        return;
      }
      this.autoplayTimer = setTimeout(() => this.goTo(this.index + 1, { manual: false }), Number(this.dataset.interval) || 5000);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.scrollTimer);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.track?.style.removeProperty('height');
      this.track?.removeAttribute('tabindex');
      this.track?.removeAttribute('aria-label');
      this.removeAttribute('data-mobile-active');
      this.removeAttribute('role');
      this.removeAttribute('aria-roledescription');
      this.slides?.forEach((slide, index) => {
        const original = this.originalAttributes[index];
        slide.inert = original.inert;
        original.hidden === null ? slide.removeAttribute('aria-hidden') : slide.setAttribute('aria-hidden', original.hidden);
      });
      this.desktopSliders?.forEach(({ slider, methods, videoAutoplay }) => {
        clearTimeout(slider.slider);
        Object.assign(slider, methods);
        slider.video_autoplay = videoAutoplay;
      });
    }
  }

  customElements.define('fox-collage-mobile', FoxCollageMobile);
})();
